import React, { useState, useEffect } from "react";
import { X, ArrowRight, Search, Info, PlayCircle, Lock, Eye, EyeOff } from "lucide-react";
import { T, primaryBtn, inputStyle, fmtDateIT } from "../theme";

export const fontImport = `@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=IBM+Plex+Sans:wght@400;500;600&display=swap');`;

export const responsiveCSS = `
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
  .grid-report-2 { display: grid; grid-template-columns: 1.4fr 1fr; gap: 14px; }
  .grid-oggi-top { display: grid; grid-template-columns: auto 1fr; gap: 24px; }
  .grid-week { display: grid; grid-template-columns: repeat(7, 1fr); gap: 8px; }
  .grid-form-3 { display: grid; grid-template-columns: 1fr 1fr 1fr auto; gap: 10px; align-items: end; }
  .grid-form-comp { display: grid; grid-template-columns: 1fr 1fr auto; gap: 10px; align-items: end; }
  .modal-card { max-width: 92vw; box-sizing: border-box; }
  .menu-toggle { display: none; }
  .sidebar-backdrop { display: none; }
  .tab-bar-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
  .tab-bar-scroll::-webkit-scrollbar { display: none; }
  .stack-on-mobile { display: flex; }
  @media (max-width: 860px) { .stack-on-mobile { flex-direction: column; } .stack-on-mobile > * { width: 100% !important; max-width: 100% !important; } }
  @media (max-width: 900px) {
    .grid-oggi-top { grid-template-columns: 1fr; }
    .grid-week { grid-template-columns: repeat(4, 1fr); }
    .sidebar { position: fixed; top: 0; left: 0; height: 100%; z-index: 60; transform: translateX(-105%); transition: transform 0.25s ease; box-shadow: 8px 0 24px rgba(30,30,60,0.25); }
    .sidebar.open { transform: translateX(0); }
    .menu-toggle { display: flex !important; }
    .sidebar-backdrop.open { display: block; position: fixed; inset: 0; background: rgba(30,30,51,0.4); z-index: 55; }
    .sidebar-close { display: flex !important; }
  }
  @media (max-width: 700px) {
    .grid-3 { grid-template-columns: 1fr 1fr; }
    .grid-report-2 { grid-template-columns: 1fr; }
    .grid-form-3 { grid-template-columns: 1fr; }
    .grid-form-comp { grid-template-columns: 1fr; }
  }
  @media (max-width: 600px) { .grid-week { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 480px) { .grid-2 { grid-template-columns: 1fr; } .grid-3 { grid-template-columns: 1fr; } .grid-week { grid-template-columns: 1fr; } }
`;

export function LogoMark({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 2.2 C6.6 2.2 2.4 6 2.4 10.7 C2.4 15.4 6.6 19.2 12 19.2 L12.9 19.2 L16.4 22.4 L16 18.4 C19.4 16.9 21.6 14 21.6 10.7 C21.6 6 17.4 2.2 12 2.2 Z" fill={T.amber} />
      <circle cx="12" cy="10.7" r="4.1" fill="none" stroke="#FFFFFF" strokeWidth="2.9" />
      <g stroke={T.amber} strokeWidth="1.3">
        <line x1="12" y1="5.1" x2="12" y2="8.6" />
        <line x1="12" y1="12.8" x2="12" y2="16.3" />
        <line x1="6.4" y1="10.7" x2="9.9" y2="10.7" />
        <line x1="14.1" y1="10.7" x2="17.6" y2="10.7" />
      </g>
    </svg>
  );
}

export function VikiBadge() {
  return (
    <span data-testid="viki-badge" style={{
      display: "inline-flex", alignItems: "center", gap: 5, background: T.surfaceAlt,
      border: `1px solid ${T.viki}55`, borderRadius: 20, padding: "3px 9px",
      fontFamily: T.fontHead, fontSize: 11, color: T.viki, letterSpacing: 0.2,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.viki, display: "inline-block" }} />
      V.I.K.I.
    </span>
  );
}

export function InfoHint({ text }) {
  return (
    <span title={text} style={{ display: "inline-flex", cursor: "help", color: T.textDim, marginLeft: 4, verticalAlign: "middle" }}>
      <Info size={12} />
    </span>
  );
}

export function StatCard({ label, value, sub, accent, testId }) {
  return (
    <div data-testid={testId} style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 16, padding: "18px 20px", boxShadow: "0 4px 16px rgba(30,30,60,0.06)" }}>
      <div style={{ color: T.textDim, fontSize: 13, marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: T.fontHead, fontSize: 28, color: T.text, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ marginTop: 8, fontSize: 13, color: accent || T.teal }}>{sub}</div>}
    </div>
  );
}

export function ScoreGauge({ value }) {
  const r = 42, c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, value)) / 100) * c;
  return (
    <svg width={104} height={104} viewBox="0 0 100 100">
      <circle cx="50" cy="50" r={r} fill="none" stroke={T.line} strokeWidth="8" />
      <circle cx="50" cy="50" r={r} fill="none" stroke={T.amber} strokeWidth="8"
        strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
        transform="rotate(-90 50 50)" style={{ transition: "stroke-dashoffset 0.5s ease" }} />
      <text x="50" y="56" textAnchor="middle" fontSize="22" fill={T.text} fontFamily={T.fontHead}>{value}</text>
    </svg>
  );
}

export function TabButton({ active, onClick, icon: Icon, children, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 8, padding: "10px 4px", marginRight: 22,
      background: "transparent", border: "none", flexShrink: 0, whiteSpace: "nowrap",
      borderBottom: active ? `2px solid ${T.amber}` : "2px solid transparent",
      color: active ? T.text : T.textDim, fontFamily: T.font, fontSize: 14, cursor: "pointer",
    }}>
      <Icon size={16} />{children}
    </button>
  );
}

export function PillButton({ active, onClick, icon: Icon, children, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 6, borderRadius: 20, padding: "7px 14px", fontSize: 13, cursor: "pointer",
      background: active ? T.amber : "transparent", color: active ? T.ink : T.textDim,
      border: `1px solid ${active ? T.amber : T.line}`, fontWeight: active ? 700 : 400,
    }}>{Icon && <Icon size={13} />} {children}</button>
  );
}

function parseItText(text) {
  const digits = String(text).replace(/\D/g, "");
  if (digits.length !== 8) return "";
  const d = +digits.slice(0, 2), m = +digits.slice(2, 4), y = +digits.slice(4);
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return "";
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export function ItalianDateInput({ value, onChange, testId }) {
  const [text, setText] = useState(value ? fmtDateIT(value) : "");
  useEffect(() => {
    if (value !== parseItText(text)) setText(value ? fmtDateIT(value) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  function handle(e) {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
    let out = digits;
    if (digits.length > 4) out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) out = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    setText(out);
    onChange(parseItText(out));
  }
  const complete = text.replace(/\D/g, "").length === 8;
  const invalid = complete && !parseItText(text);
  return (
    <input data-testid={testId} inputMode="numeric" placeholder="gg/mm/aaaa" value={text} onChange={handle}
      style={{ ...inputStyle, borderColor: invalid ? T.rose : T.line }} title={invalid ? "Data non valida" : "Formato: gg/mm/aaaa"} />
  );
}

export function PasswordField({ value, onChange, placeholder, testId }) {  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <Lock size={14} color={T.textDim} style={{ position: "absolute", left: 10, top: 11 }} />
      <input data-testid={testId} type={show ? "text" : "password"} value={value} onChange={onChange}
        style={{ ...inputStyle, paddingLeft: 30, paddingRight: 34 }} placeholder={placeholder} />
      <button type="button" onClick={() => setShow((s) => !s)} tabIndex={-1}
        title={show ? "Nascondi password" : "Mostra password"}
        style={{ position: "absolute", right: 8, top: 8, background: "transparent", border: "none", color: T.textDim, cursor: "pointer", padding: 2, display: "flex" }}>
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
}

export function ConfirmDialog({ title, body, confirmLabel, onConfirm, onCancel }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(30,30,51,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
      <div className="modal-card" data-testid="confirm-dialog" style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 12, padding: 22, width: 340, boxShadow: "0 16px 40px rgba(30,30,60,0.25)" }}>
        <div style={{ fontFamily: T.fontHead, fontSize: 16, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 13, color: T.textDim, marginBottom: 20, lineHeight: 1.5 }}>{body}</div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button data-testid="confirm-dialog-cancel" onClick={onCancel} style={{ background: "transparent", border: `1px solid ${T.line}`, color: T.textDim, borderRadius: 7, padding: "8px 14px", fontSize: 13, cursor: "pointer" }}>Annulla</button>
          <button data-testid="confirm-dialog-confirm" onClick={onConfirm} style={{ background: T.rose, color: T.ink, border: "none", borderRadius: 7, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

export const tabTours = {
  oggi: [
    { title: "Il punteggio profilo", text: "Un numero da 0 a 100 che riassume come stai andando: azioni fatte, dati registrati, cliente ideale definito. Sale man mano che usi l'app." },
    { title: "Il suggerimento di V.I.K.I.", text: "Ogni giorno una sola azione e una sola idea di contenuto, scelte per te. Non un elenco da cui scegliere: solo la mossa di oggi." },
    { title: "La serie attiva", text: "Conta i giorni consecutivi in cui hai fatto almeno un'azione. È un promemoria gentile, non un obbligo." },
  ],
  contenuti: [
    { title: "Le idee pronte", text: "Scegli un tipo di contenuto e trovi idee già scritte, pronte da assegnare a un giorno della settimana." },
    { title: "Compila la settimana per me", text: "Non hai tempo di scegliere? Questo pulsante riempie da solo i giorni vuoti, dando priorità ai giorni migliori." },
    { title: "Dalla bozza alla pubblicazione", text: "Ogni idea nel calendario passa da bozza ad approvato; se colleghi Instagram, compare \"Pubblica ora\": un solo pulsante, non un menu da capire." },
  ],
  crescita: [
    { title: "La tua strategia su misura", text: "In alto, V.I.K.I. scrive una strategia di due settimane guardando i dati reali del tuo profilo — non un consiglio generico uguale per tutti." },
    { title: "Il piano consigliato", text: "5 azioni scelte per il tuo obiettivo. Le altre tecniche restano disponibili, ma non ingombrano la vista." },
    { title: "Le campagne pronte", text: "Passa alla pillola \"Campagne\" per modelli di più giorni già pronti; quella evidenziata è la più adatta a te." },
  ],
  andamento: [
    { title: "Sincronizza o inserisci a mano", text: "Se hai collegato Instagram, un tocco basta per aggiornare i numeri. Altrimenti puoi sempre inserirli tu." },
    { title: "Il tuo engagement nel contesto giusto", text: "Un'etichetta ti dice se il tuo tasso di interazione è nella media — calcolata sulla tua fascia di follower, non un numero uguale per tutti." },
    { title: "Il confronto con un concorrente", text: "Aggiungi i follower di un profilo simile al tuo per capire se stai crescendo più o meno velocemente di chi fa il tuo stesso lavoro." },
  ],
  profilo: [
    { title: "Il cliente ideale", text: "Descrivi chi vuoi raggiungere: guida il tono e i contenuti che V.I.K.I. ti suggerisce." },
    { title: "Il link in bio", text: "Passa alla pillola \"Link in bio\" per creare una mini pagina con tutti i tuoi link, pronta da mettere al posto di uno solo." },
  ],
};

export function TourModal({ steps, onClose }) {
  const [step, setStep] = useState(0);
  const s = steps[step];
  const isLast = step === steps.length - 1;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(30,30,51,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
      <div className="modal-card" data-testid="tour-modal" style={{ background: T.surface, border: `1px solid ${T.amber}`, borderRadius: 14, padding: 26, width: 400, boxShadow: "0 20px 50px rgba(30,30,60,0.25)" }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
          {steps.map((_, i) => <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= step ? T.amber : T.line }} />)}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <PlayCircle size={16} color={T.amber} />
          <span style={{ fontSize: 11, color: T.textDim }}>Passo {step + 1} di {steps.length}</span>
        </div>
        <div style={{ fontFamily: T.fontHead, fontSize: 17, marginBottom: 10 }}>{s.title}</div>
        <div style={{ fontSize: 14, color: T.textDim, lineHeight: 1.6, marginBottom: 22 }}>{s.text}</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button data-testid="tour-skip-btn" onClick={onClose} style={{ background: "transparent", border: "none", color: T.textDim, fontSize: 13, cursor: "pointer", padding: 0 }}>Salta</button>
          <div style={{ display: "flex", gap: 8 }}>
            {step > 0 && <button onClick={() => setStep(step - 1)} style={{ background: "transparent", border: `1px solid ${T.line}`, color: T.textDim, borderRadius: 7, padding: "8px 14px", fontSize: 13, cursor: "pointer" }}>Indietro</button>}
            <button data-testid="tour-next-btn" onClick={() => (isLast ? onClose() : setStep(step + 1))} style={{ ...primaryBtn, display: "flex", alignItems: "center", gap: 6 }}>
              {isLast ? "Ho capito" : "Avanti"} {!isLast && <ArrowRight size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CommandPalette({ commands, onClose }) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const filtered = commands.filter((c) => c.label.toLowerCase().includes(query.toLowerCase()));
  useEffect(() => { setActiveIndex(0); }, [query]);

  function handleKeyDown(e) {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (filtered[activeIndex]) { filtered[activeIndex].action(); onClose(); } }
    else if (e.key === "Escape") { onClose(); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(30,30,51,0.5)", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "12vh", zIndex: 110 }} onClick={onClose}>
      <div className="modal-card" data-testid="command-palette" onClick={(e) => e.stopPropagation()} style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 12, width: 480, maxHeight: "60vh", overflow: "hidden", boxShadow: "0 20px 50px rgba(30,30,60,0.3)", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: `1px solid ${T.line}` }}>
          <Search size={16} color={T.textDim} />
          <input data-testid="command-palette-input" autoFocus value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={handleKeyDown}
            placeholder="Cerca una scheda, un profilo, un'azione…"
            style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: T.text, fontSize: 14, fontFamily: T.font }} />
        </div>
        <div style={{ overflowY: "auto", padding: 6 }}>
          {filtered.length === 0 && <div style={{ padding: "14px 12px", fontSize: 13, color: T.textDim }}>Nessun risultato.</div>}
          {filtered.map((c, i) => (
            <button key={c.label} onClick={() => { c.action(); onClose(); }} onMouseEnter={() => setActiveIndex(i)} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", textAlign: "left",
              padding: "10px 12px", borderRadius: 7, background: i === activeIndex ? T.surfaceAlt : "transparent",
              border: "none", color: T.text, fontSize: 13, cursor: "pointer",
            }}>
              <span>{c.label}</span>
              {c.hint && <span style={{ fontSize: 11, color: T.textDim }}>{c.hint}</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function VersionBadge({ version, changelog }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="no-print" style={{ position: "fixed", top: 14, right: 16, zIndex: 40 }}>
      <button data-testid="version-badge" onClick={() => setOpen((o) => !o)} style={{
        background: T.surface, border: `1px solid ${T.line}`, color: T.textDim,
        borderRadius: 20, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontFamily: T.fontHead,
      }}>{version}</button>
      {open && (
        <div style={{ position: "absolute", top: 30, right: 0, width: 280, background: T.surface, border: `1px solid ${T.line}`, borderRadius: 10, padding: 14, boxShadow: "0 8px 24px rgba(30,30,60,0.2)" }}>
          <div style={{ fontFamily: T.fontHead, fontSize: 13, marginBottom: 10, color: T.text }}>Novità di questa versione</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {(changelog || []).map((c, i) => (
              <div key={i} style={{ fontSize: 12, color: T.textDim, display: "flex", gap: 6, lineHeight: 1.4, textAlign: "left" }}>
                <span style={{ color: T.amber, flexShrink: 0 }}>—</span>{c}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function CloseX({ onClick }) {
  return (
    <button onClick={onClick} style={{ position: "absolute", top: -6, right: -6, background: "transparent", border: "none", color: T.textDim, cursor: "pointer", padding: 6 }}><X size={18} /></button>
  );
}

export function LighthouseScene() {
  return (
    <svg viewBox="0 0 800 1000" preserveAspectRatio="xMidYMid slice" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
      <defs>
        <linearGradient id="sky" x1="0.2" y1="0" x2="0.8" y2="1">
          <stop offset="0%" stopColor="#E4E7FA" /><stop offset="50%" stopColor="#F1E9FA" /><stop offset="100%" stopColor="#FDE9E4" />
        </linearGradient>
        <radialGradient id="glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FF6B35" stopOpacity="0.32" /><stop offset="100%" stopColor="#FF6B35" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="vignette" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#EEF0FB" stopOpacity="0.94" /><stop offset="42%" stopColor="#EEF0FB" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect width="800" height="1000" fill="url(#sky)" />
      <circle cx="150" cy="200" r="50" fill="#FFB199" opacity="0.8" />
      <circle cx="150" cy="200" r="86" fill="#FF6B35" opacity="0.14" />
      {[[590, 130, 1.1], [670, 230, 0.7], [90, 330, 0.8], [720, 350, 0.9]].map(([cx, cy, s], i) => (
        <g key={i} opacity="0.85">
          <ellipse cx={cx} cy={cy} rx={40 * s} ry={16 * s} fill="#FFFFFF" />
          <ellipse cx={cx + 26 * s} cy={cy + 4 * s} rx={26 * s} ry={13 * s} fill="#FFFFFF" />
          <ellipse cx={cx - 24 * s} cy={cy + 5 * s} rx={22 * s} ry={11 * s} fill="#FFFFFF" />
        </g>
      ))}
      {[[330, 250], [365, 228], [270, 292]].map(([x, y], i) => (
        <path key={i} d={`M${x - 10} ${y} Q${x - 5} ${y - 8} ${x} ${y} Q${x + 5} ${y - 8} ${x + 10} ${y}`} stroke="#8B87B3" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.55" />
      ))}
      {/* nodi social collegati al fumetto */}
      <g stroke="#C9C4EE" strokeWidth="3" strokeDasharray="2 8" strokeLinecap="round" opacity="0.9">
        <line x1="400" y1="430" x2="205" y2="330" />
        <line x1="400" y1="430" x2="612" y2="352" />
        <line x1="400" y1="430" x2="580" y2="580" />
        <line x1="400" y1="430" x2="222" y2="560" />
      </g>
      {[[205, 330, "#7C4DFF"], [612, 352, "#06A66B"], [580, 580, "#FFB199"], [222, 560, "#8B87B3"]].map(([cx, cy, c], i) => (
        <g key={i}>
          <circle cx={cx} cy={cy} r="26" fill="#FFFFFF" />
          <circle cx={cx} cy={cy} r="9" fill={c} opacity="0.9" transform={`translate(0,-4)`} />
          <path d={`M${cx - 13} ${cy + 16} Q${cx} ${cy - 2} ${cx + 13} ${cy + 16}`} fill={c} opacity="0.9" />
        </g>
      ))}
      {/* fumetto So.Help con salvagente */}
      <circle cx="400" cy="430" r="150" fill="url(#glow)" />
      <path d="M400 320 C330 320 276 368 276 428 C276 488 330 536 400 536 L412 536 L456 576 L451 526 C494 507 524 470 524 428 C524 368 470 320 400 320 Z" fill="#FF6B35" />
      <circle cx="400" cy="428" r="52" fill="none" stroke="#FFFFFF" strokeWidth="36" />
      <g stroke="#FF6B35" strokeWidth="14">
        <line x1="400" y1="356" x2="400" y2="402" />
        <line x1="400" y1="454" x2="400" y2="500" />
        <line x1="328" y1="428" x2="374" y2="428" />
        <line x1="426" y1="428" x2="472" y2="428" />
      </g>
      {/* onde */}
      <g stroke="#FFFFFF" strokeWidth="10" fill="none" strokeLinecap="round" opacity="0.9">
        <path d="M60 680 Q110 660 160 680 T260 680 T360 680" />
        <path d="M440 700 Q490 680 540 700 T640 700 T740 700" />
        <path d="M180 750 Q230 730 280 750 T380 750" />
      </g>
      <g opacity="0.92">
        <rect x="0" y="800" width="100" height="200" rx="6" fill="#FFD9C7" />
        <rect x="106" y="760" width="70" height="240" rx="6" fill="#C9F0DE" />
        <rect x="182" y="830" width="80" height="170" rx="6" fill="#E3DEFB" />
        <rect x="600" y="780" width="90" height="220" rx="6" fill="#D6E0FB" />
        <rect x="696" y="820" width="85" height="180" rx="6" fill="#FFE1C9" />
        {[[24, 830], [44, 830], [64, 860], [24, 890], [132, 790], [152, 810], [152, 850], [622, 810], [642, 810], [662, 850], [718, 850], [738, 880]].map(([x, y], i) => (
          <rect key={i} x={x} y={y} width="8" height="10" rx="2" fill="#FFFFFF" opacity="0.8" />
        ))}
      </g>
      <rect width="800" height="1000" fill="url(#vignette)" />
    </svg>
  );
}
